// src/ScrollOptimizer.js
// Global performance fix for smooth scrolling across the entire site.
// Import once in src/index.js — no other files need changes.

import { useEffect } from 'react';

const ScrollOptimizer = () => {
  useEffect(() => {
    /* ------------------------------------------------------------------
       1. INJECT A SINGLE GLOBAL STYLESHEET THAT OVERRIDES THE EXPENSIVE
          CSS PROPERTIES CAUSING SCROLL LAG.
       ------------------------------------------------------------------ */
    const STYLE_ID = 'scroll-optimizer-style';
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.innerHTML = `
        /* -------- 1) KILL THE PARALLAX (biggest lag source) -------- */
        .homepage-hero-slide-image,
        .homepage-hero-slide-video,
        .gallery-hero-slide-bg,
        .about-page-hero,
        .trainee-stats-hero,
        .header,
        .top-bar {
          background-attachment: scroll !important;
        }

        /* -------- 2) DISABLE backdrop-filter on big surfaces -------- */
        /* Small buttons keep a light blur — large panels do not. */
        .header,
        .header-scrolled,
        .top-bar,
        .gallery-filters-section,
        .search-modal-overlay,
        .homepage-event-modal-overlay,
        .homepage-activity-modal-overlay,
        .gallery-modal-overlay,
        .trainee-stats-modal-overlay,
        .trainee-stats-toast,
        .search-modal-content,
        .gallery-modal-content,
        .homepage-event-modal,
        .homepage-activity-modal,
        .trainee-stats-modal {
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
        }

        /* -------- 3) PROMOTE ONLY THE RIGHT LAYERS -------- */
        /* Force GPU compositing on the slider track and marquees,
           then prevent "will-change" on everything else. */
        * { will-change: auto !important; }

        .homepage-hero-slides,
        .homepage-women-marquee-track,
        .homepage-events-scroll-track,
        .gallery-hero-slides,
        .about-page-partners-marquee-track {
          will-change: transform !important;
          transform: translateZ(0);
          backface-visibility: hidden;
        }

        /* -------- 4) REMOVE SMOOTH SCROLL HIJACKING -------- */
        html, body {
          scroll-behavior: auto !important;
          overscroll-behavior-y: none;
        }

        /* -------- 5) PAUSE HEAVY ANIMATIONS BY DEFAULT -------- */
        .homepage-women-marquee-track,
        .homepage-events-scroll-track,
        .about-page-partners-marquee-track,
        .gallery-hero-slides,
        .homepage-hero-slides {
          animation-play-state: paused !important;
        }
        /* They will be re-enabled by the JS below when visible. */

        /* -------- 6) CHEAPEN SHADOWS ON SCROLL-INTENSIVE CARDS -------- */
        @media (max-width: 1200px) {
          .homepage-stat-card:hover,
          .homepage-department-card:hover,
          .homepage-event-card:hover,
          .homepage-woman-card:hover,
          .gallery-grid-item:hover,
          .about-page-pillar-card:hover,
          .about-page-value-card:hover,
          .about-page-team-card:hover,
          .about-page-partner-card:hover {
            box-shadow: 0 6px 18px rgba(0,0,0,0.10) !important;
            transform: translateY(-3px) !important;
          }
        }

        /* -------- 7) CHEAPEN HOVER TRANSFORMS GLOBALLY -------- */
        /* Cards scale on hover -> repaint. Reduce cost by using translate
           instead of scale on the inner image, and shorten transition. */
        .homepage-gallery-image img,
        .homepage-department-image img,
        .homepage-event-image img,
        .gallery-image,
        .about-page-pillar-card .about-page-card-icon-wrap {
          transition-duration: 0.35s !important;
        }

        /* -------- 8) AVOID REPAINT ON PSEUDO-ELEMENT FILTERS -------- */
        .homepage-cta::before,
        .trainee-stats-hero::before,
        .about-page-hero::before {
          display: none !important;
        }
      `;
      document.head.appendChild(style);
    }

    /* ------------------------------------------------------------------
       2. PAUSE / RESUME ANIMATIONS AND MARQUEES BASED ON VISIBILITY.
          This is the single biggest improvement for scroll smoothness
          on long pages with infinite marquees.
       ------------------------------------------------------------------ */
    const animatedSelectors = [
      '.homepage-women-marquee-track',
      '.homepage-events-scroll-track',
      '.about-page-partners-marquee-track',
      '.gallery-hero-slides',
      '.homepage-hero-slides'
    ];

    const resumeFor = (rootEl) => {
      rootEl.querySelectorAll(animatedSelectors.join(',')).forEach((el) => {
        el.style.setProperty('animation-play-state', 'running', 'important');
      });
    };
    const pauseFor = (rootEl) => {
      rootEl.querySelectorAll(animatedSelectors.join(',')).forEach((el) => {
        el.style.setProperty('animation-play-state', 'paused', 'important');
      });
    };

    /* Use a single observer that watches every section containing an
       animation. When the section is off-screen, pause its animations. */
    const animationObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            resumeFor(entry.target);
          } else {
            pauseFor(entry.target);
          }
        });
      },
      { rootMargin: '200px 0px', threshold: 0 }
    );

    const watchContainers = () => {
      const containers = document.querySelectorAll(
        '.homepage-women, .homepage-events, .about-page-partners-section, .gallery-hero-section, .homepage-hero'
      );
      containers.forEach((el) => animationObserver.observe(el));
    };

    /* ------------------------------------------------------------------
       3. THROTTLE / DEBOUNCE ALL SCROLL LISTENERS GLOBALLY.
          Some components attach their own scroll handlers. We can't
          rewrite them from here, but we CAN stop the browser from firing
          them faster than ~60fps by debouncing the "scroll" event on the
          window with a passive listener that doesn't hijack behaviour.
       ------------------------------------------------------------------ */
    let ticking = false;
    const passiveScrollHandler = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
      });
    };
    window.addEventListener('scroll', passiveScrollHandler, { passive: true });

    /* ------------------------------------------------------------------
       4. LAZY-ISH IMAGE DECODING — force all existing <img> to use
          async decoding so the main thread isn't blocked on paint.
       ------------------------------------------------------------------ */
    const tagImages = () => {
      document.querySelectorAll('img:not([decoding])').forEach((img) => {
        img.decoding = 'async';
        if (!img.loading) img.loading = 'lazy';
      });
    };

    /* ------------------------------------------------------------------
       5. KICK EVERYTHING OFF.
       ------------------------------------------------------------------ */
    // Wait one frame so React has finished its initial paint.
    const raf = requestAnimationFrame(() => {
      watchContainers();
      tagImages();
    });

    /* Re-run observers + tagger whenever the DOM changes
       (e.g. when navigating between routes). */
    const mutationObserver = new MutationObserver(() => {
      watchContainers();
      tagImages();
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    /* ------------------------------------------------------------------
       6. CLEANUP
       ------------------------------------------------------------------ */
    return () => {
      cancelAnimationFrame(raf);
      animationObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('scroll', passiveScrollHandler);
    };
  }, []);

  return null;
};

export default ScrollOptimizer;